<template>
  <footer class="lefebvre-footer-component">
    <div class="info-wrapper">
      <div class="information">
        <section class="logo-and-social-media">
          <LefebvreLogoComponent class="lefebvre-logo" />
          <ul class="social-media-list">
            <li v-for="(network, index) in socialNetworkLinks" :key="`${index}-network`" class="network-item">
              <a :class="network.icon" class="network-link" :href="network.url" target="_blank">
                <img :src="network.icon" :alt="network.name" />
                <span class="screen-reader-only">{{ network.name }}</span>
              </a>
            </li>
          </ul>
        </section>
        <section class="text-container">
          <div class="policy-container">
            <p class="copyright">© Lefebvre {{ currentYear }}. {{ texts.copyright }}</p>
            <template v-for="(link, index) in footerLinks">
              <a
                :key="`${index}-footer-link`"
                class="footer-link"
                :href="link.url"
                target="_blank"
                @click="handleLinkClick($event, link)"
              >
                <span>{{ $t(link.name) }}</span>
              </a>
            </template>
          </div>
          <div v-if="lastAccess" class="connection-container">{{ lastAccessText }}</div>
        </section>
      </div>
    </div>
    <div class="border" />
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import store from '@/store/store'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { FooterLink } from './types/FooterComponentTypes'
import { externalURLS, URLS } from '@/router/routes/urlRoutes'
import useRouter from '@/composables/useRouter'
import { useI18n } from 'vue-i18n-composable'
import { imageURL } from '@/assets/images/url/imageURL'
import LefebvreLogoComponent from '@/components/Logo/LefebvreLogoComponent.vue'

// // COMPOSABLES
const { goToRoute } = useRouter()

// TRANSLATIONS
const { t } = useI18n()

const texts = {
  copyright: t('views.login.copyright_text'),
  lastAccess: t('views.last_access')
}

// // CONSTANTS
const authModule = ModuleNamespaces.AUTH

const footerLinks: FooterLink[] = [
  {
    name: 'views.login.footer.legal_warning',
    url: externalURLS.legal_warning
  },
  {
    name: 'views.login.footer.privacy_policy',
    url: externalURLS.privacy_policy
  },
  {
    name: 'views.login.footer.cookies_policy',
    url: externalURLS.cookies_policy,
    callback: process.env.VUE_APP_COOKIEBOT_ID
      ? (): void => {
          goToRoute({
            name: URLS.COOKIES_POLICY
          })
        }
      : false
  }
]

const socialNetworkLinks: FooterLink[] = [
  {
    name: 'Instagram',
    icon: imageURL.instagram,
    url: externalURLS.instagram
  },
  {
    name: 'Facebook',
    icon: imageURL.facebook,
    url: externalURLS.facebook
  },
  {
    name: 'Linkedin',
    icon: imageURL.linkedin,
    url: externalURLS.linkedin
  },
  {
    name: 'X',
    icon: imageURL.twitter,
    url: externalURLS.twitter
  },
  {
    name: 'Youtube',
    icon: imageURL.youtube,
    url: externalURLS.youtube
  }
]

// // COMPUTED PROPERTIES
const lastAccess = computed(() => store.getters[`${authModule}/getLastAccess`])

const currentYear = computed(() => {
  const year = new Date().getFullYear()
  return year
})

const lastAccessText = computed(() => {
  return `${texts.lastAccess} ${lastAccess.value}`
})

// FUNCTIONS
const handleLinkClick = (event: MouseEvent, link: FooterLink): void => {
  if (link.callback) {
    event.preventDefault()
    link.callback()
  }
}
</script>

<style scoped lang="scss">
.lefebvre-footer-component {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-top: 1px solid $stroke-weak-color;

  .info-wrapper {
    display: flex;
    padding: $spacing-xs $spacing-xxl;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    align-self: stretch;

    .information {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: $spacing-sm;
      align-self: stretch;

      .logo-and-social-media {
        display: flex;
        padding: $spacing-xs 0;
        flex-direction: column;
        align-items: flex-start;
        gap: $spacing-md;
        align-self: stretch;

        .lefebvre-logo {
          --logo-desktop-width: 149px;
        }

        .social-media-list {
          @include list;
          display: flex;
          align-items: flex-start;
          gap: $spacing-sm;

          .network-link {
            display: flex;
            width: 32px;
            height: 32px;
            justify-content: center;
            align-items: center;
            aspect-ratio: 1/1;
            text-decoration: none;
            cursor: pointer;
          }
        }
      }

      .text-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        align-self: stretch;

        .policy-container {
          display: flex;
          min-height: 28px;
          padding: 0;
          align-items: center;
          align-self: stretch;
          color: $text-weak;
          font-feature-settings: 'liga' off, 'clig' off;
          font-family: $inter-font;
          font-size: $fontSize-xs;
          font-style: normal;
          font-weight: 400;
          line-height: 20px;
          flex-wrap: wrap;

          .copyright {
            margin: 0 7px 0 0;
          }

          .footer-link {
            text-decoration: none;
            color: $text-weak;
            cursor: pointer;
            display: inline;

            &:not(:first-of-type)::before {
              content: '·';
              margin: 0 5px;
            }
          }
        }

        .connection-container {
          display: flex;
          padding: $spacing-xxs 0px;
          justify-content: flex-end;
          align-items: center;
          color: $main-1000;
          text-align: right;
          font-feature-settings: 'liga' off, 'clig' off;
          font-family: $inter-font;
          font-size: $fontSize-xs;
          font-style: normal;
          font-weight: 400;
          line-height: 20px;
        }
      }
    }
  }

  .border {
    height: 12px;
    width: 100%;
    background: $horizontal-gradient;
  }
}

@media (min-width: 768px) {
  .lefebvre-footer-component {
    height: 136px;
    max-height: 136px;

    .info-wrapper {
      padding: $spacing-xs 48px;

      .information {
        .logo-and-social-media {
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
        }

        .text-container {
          flex-direction: row;

          .policy-container {
            padding-right: $spacing-md;
            flex: 1 0 0;
          }
        }
      }
    }
  }
}

@media (min-width: 1440px) {
  .lefebvre-footer-component {
    height: 132px;
    max-height: 132px;

    .info-wrapper {
      padding: $spacing-xs 120px;

      .information {
        .text-container {
          padding: 3px 0;
        }
      }
    }
  }
}
</style>
